import * as React from 'react';
import titleTemplate from 'lib/title-template';
import Head from 'next/head';
import Page from 'components/common/page';
import Link from 'next/link';
import css from './terms-privacy.module.css';

const PrivacyPolicy = (): JSX.Element => {
  return (
    <Page pageName="Privacy">
      <Head>
        <title>{titleTemplate('Privacy Policy')}</title>
        <meta
          name="description"
          content="Thank you for using Bibcitation! Holding onto your private information is a serious responsibility, so we handle it with extra care."
        />
      </Head>
      <div id={css.container}>
        <div className={css.contentTitle}>Privacy Policy</div>
        <div className={css.contentText} style={{ textAlign: 'center' }}>
          Last updated: September 24, 2020.
        </div>
        <div className={css.contentText}>
          <br />
          <br />
          Thank you for using Bibcitation! We&apos;re happy you&apos;re here.
          Holding onto your private information is a serious responsibility, and
          we want you to know how we handle it.
        </div>
        <div className={css.contentSubTitle}>
          What information Bibcitation collects and why
        </div>
        <div className={css.contentText}>
          <strong>Information from website browsers</strong>
          <br />
          If you&apos;re just browsing the website, we collect the same basic
          information that most websites collect by using common internet
          technologies, such as cookies and web server logs. The information we
          collect about all visitors to our website includes the visitor’s
          browser type, language preference, referring site, additional websites
          requested, and the date and time of each visitor request. We also
          collect potentially personally-identifying information like Internet
          Protocol (IP) addresses.
          <br />
          <br />
          <strong>Why do we collect this?</strong>
          <br />
          We collect this information to better understand how our website
          visitors use Bibcitation and to monitor and protect the security of
          the website.
          <div className={css.contentSubTitle}>
            What information Bibcitation does not collect
          </div>
          <div className={css.contentText}>
            We do not intentionally collect sensitive personal information, such
            as social security numbers, genetic data, health information, or
            religious information. Although Bibcitation does not request or
            intentionally collect any sensitive personal information, we realize
            that you might send sensitive information.
            <br />
            <br />
            If you&apos;re a child under the age of 13, you may not have an
            account on Bibcitation. Bibcitation does not knowingly collect
            information from or direct any of our content specifically to
            children under 13. If we learn or have reason to suspect that you
            are a user who is under the age of 13, we will, unfortunately, have
            to close your account. Please see our{' '}
            <Link href="/terms">
              <a>Terms of Service</a>
            </Link>{' '}
            for information about account termination.
          </div>
          <div className={css.contentSubTitle}>
            How we share the information we collect
          </div>
          <div className={css.contentText}>
            We use various third-party ad networks to display advertisements on
            Bibcitation. These third-party ad networks might make use of the
            tracking cookies to show targeted advertisements to you. We do not
            share ANY information about you to these third-party networks.
          </div>
          <div className={css.contentSubTitle}>
            Our use of cookies and tracking
          </div>
          <div className={css.contentText}>
            <strong>Cookies</strong>
            <br />
            Bibcitation uses cookies to make interactions with our service easy
            and meaningful. We use cookies (and similar technologies, like HTML5
            localStorage and sessionStorage) to keep you logged in, remember
            your preferences, and provide information for future development of
            Bibcitation.
            <br />
            <br />A cookie is a small piece of text that our web server stores
            on your computer or mobile device, which your browser sends to us
            when you return to our site. Cookies do not necessarily identify you
            if you are merely visiting Bibcitation; however, a cookie may store
            a unique identifier for each logged in user. The cookies Bibcitation
            sets are essential for the operation of the website, or are used for
            performance or functionality. By using our website, you agree that
            we can place these types of cookies on your computer or device. If
            you disable your browser or device’s ability to accept cookies, you
            will not be able to log in or use Bibcitation’s services.
            <br />
            <br />
            <strong>Tracking Tools</strong>
            <br />
            We use Tracking Tools (such as{' '}
            <a
              href="https://analytics.google.com/"
              target="_blank"
              rel="noreferrer"
            >
              Google Analytics
            </a>
            ,{' '}
            <a href="https://www.hotjar.com" target="_blank" rel="noreferrer">
              Hotjar
            </a>
            , or{' '}
            <a href="https://amplitude.com/" target="_blank" rel="noreferrer">
              Amplitude
            </a>
            ) as third party tracking services. We use Tracking Tools to collect
            information about how our website performs and how our users, in
            general, navigate through and use Bibcitation. This helps us
            evaluate our users&apos; use of Bibcitation; compile statistical
            reports on activity; and improve our content and website
            performance.
            <br />
            <br />
            Certain pages on our site may set other third-party cookies. For
            example, we may embed content, such as videos, from another site
            that sets a cookie. While we try to minimize these third party
            cookies, we can’t always control what cookies this third party
            content sets.
          </div>
          <div className={css.contentSubTitle}>
            How Bibcitation secures your information
          </div>
          <div className={css.contentText}>
            Bibcitation takes all measures reasonably necessary to protect user
            information from unauthorized access, alteration, or destruction;
            maintain data accuracy; and help ensure the appropriate use of user
            information. We follow generally accepted industry standards to
            protect the personal information submitted to us, both during
            transmission and once we receive it. No method of transmission, or
            method of electronic storage, is 100% secure. Therefore, we cannot
            guarantee its absolute security.
          </div>
          <div className={css.contentSubTitle}>
            Bibcitation&apos;s global privacy practices
          </div>
          <div className={css.contentText}>
            Information that we collect will be stored and processed in the
            United States in accordance with this Privacy Policy. However, we
            understand that we have users from different countries and regions
            with different privacy expectations, and we try to meet those needs.
            <br />
            <br />
            We provide the same standard of privacy protection to all our users
            around the world, regardless of their country of origin or location,
            and we are proud of the levels of notice, choice, accountability,
            security, data integrity, access, and recourse we provide. We work
            hard to comply with the applicable data privacy laws wherever we do
            business.
            <br />
            <br />
            Additionally, we require that if our vendors or affiliates have
            access to User Personal Information, they must comply with our
            privacy policies and with applicable data privacy laws, including
            signing data transfer agreements such as Standard Contractual Clause
            agreements.
          </div>
          <div className={css.contentSubTitle}>Resolving Complaints</div>
          <div className={css.contentText}>
            If you have concerns about the way Bibcitation is handling your user
            information, please let us know immediately. We want to help. You
            may contact us directly through our{' '}
            <Link href="/contact">
              <a>contact us</a>
            </Link>{' '}
            page with the subject line &quot;Privacy Concerns.&quot; We will
            respond within 45 days at the latest. In the unlikely event that a
            dispute arises between you and Bibcitation regarding our handling of
            your user information, we will do our best to resolve it.
          </div>
          <div className={css.contentSubTitle}>
            How we respond to compelled disclosure
          </div>
          <div className={css.contentText}>
            Bibcitation may disclose personally-identifying information or other
            information we collect about you to law enforcement in response to a
            valid subpoena, court order, warrant, or similar government order,
            or when we believe in good faith that disclosure is reasonably
            necessary to protect our property or rights, or those of third
            parties or the public at large. In complying with court orders and
            similar legal processes, Bibcitation strives for transparency. When
            permitted, we will make a reasonable effort to notify users of any
            disclosure of their information, unless we are prohibited by law or
            court order from doing so, or in rare, exigent circumstances.
          </div>
          <div className={css.contentSubTitle}>How we communicate with you</div>
          <div className={css.contentText}>
            Depending on your email settings, Bibcitation may occasionally send
            notification emails and send marketing emails, but only with your
            consent. There&apos;s an unsubscribe link located at the bottom of
            each of the emails we send you. Our emails might contain a pixel
            tag, which is a small, clear image that can tell us whether or not
            you have opened an email and what your IP address is. We use this
            pixel tag to make our email more effective for you and to make sure
            we’re not sending you unwanted email. If you prefer not to receive
            pixel tags, please opt out of marketing emails.
          </div>
          <div className={css.contentSubTitle}>
            Changes to our Privacy Policy
          </div>
          <div className={css.contentText}>
            Although most changes are likely to be minor, Bibcitation may change
            our Privacy Policy from time to time. We will provide notification
            to Users of material changes to this Privacy Policy through our
            Website at least 24 hours prior to the change taking effect by
            posting a notice on our home page or sending email to the email
            address specified in your Bibcitation primary account if you are
            subscribed to emails. For changes to this Privacy Policy that do not
            affect your rights, we encourage visitors to check this page
            frequently.
          </div>
          <div className={css.contentSubTitle}>Contacting Bibcitation</div>
          <div className={css.contentText}>
            Questions regarding Bibcitation&apos;s Privacy Policy or information
            practices should be directed to our email{' '}
            <a href="mailto:admin@bibcitation.com">admin@bibcitation.com</a> or
            our{' '}
            <Link href="/contact">
              <a>contact form</a>
            </Link>
            .
          </div>
        </div>
      </div>
    </Page>
  );
};
export default PrivacyPolicy;
